import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faTrash, faCheckDouble } from '@fortawesome/free-solid-svg-icons';
import socket from '../../utils/socket';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { API_URL } from '../../config';
import BrandLogo from '../../shared-ui/BrandLogo';
import { GlobalSearchModal } from '../../shared-ui/GlobalSearchModal';
import { AppLauncher } from '../../shared-ui/AppLauncher';
import { fetchTenantBranding, tenantHintFromStorage } from '../../shared-ui/tenantBranding';
import platformLogo from '../../assets/branding/cranesoftwares-logo.png';
import '../../shared-ui/crane-tokens.css';
import './Navbar.css';

const API_BASE = API_URL.replace(/\/api\/?$/, '');
const ZTOOL_URL = process.env.REACT_APP_ZTOOL_URL;
const AUTH_API = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL || process.env.REACT_APP_API_BASE_URL || '';

const Navbar = () => {
    const [notifications, setNotifications] = useState([]);
    const [isNotificationVisible, setNotificationVisible] = useState(false);
    const [hideTimeoutId, setHideTimeoutId] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [tenantName, setTenantName] = useState('');
    const navigate = useNavigate();

    const getToken = useCallback(() => localStorage.getItem('zTaskAuthToken') || '', []);

    const getUserDetails = () => {
        const userDetailsString = localStorage.getItem('zTaskUserDetails');
        if (userDetailsString) {
            try {
                return JSON.parse(userDetailsString);
            } catch (error) {
                return null;
            }
        }
        return null;
    };

    const currentUser = getUserDetails();
    const userId = currentUser ? currentUser.id : null;
    const token = getToken();

    useEffect(() => {
        const onKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    useEffect(() => {
        const hint = tenantHintFromStorage();
        if (hint) setTenantName(hint);
        let cancelled = false;
        fetchTenantBranding([AUTH_API, API_BASE]).then(({ name }) => {
            if (!cancelled && name) setTenantName(name);
        });
        return () => { cancelled = true; };
    }, [token]);

    useEffect(() => {
        if (!userId || !token) return;
        const fetchNotifications = async () => {
            try {
                const res = await axios.get(`${API_BASE}/api/notifications`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setNotifications(res.data || []);
            } catch (err) {
                console.error('Failed to fetch notifications:', err);
            }
        };
        fetchNotifications();
    }, [userId, token]);

    useEffect(() => {
        if (!userId) return;
        const handleNewNotification = (data) => {
            setNotifications((prev) => [data, ...prev]);
        };
        socket.on('notification', handleNewNotification);
        return () => {
            socket.off('notification', handleNewNotification);
        };
    }, [userId]);

    const handleNotificationClick = () => {
        navigate('/dashboard');
        setNotificationVisible(false);
    };

    const markAsSeen = async (notificationId, event) => {
        event.stopPropagation();
        try {
            await axios.put(
                `${API_BASE}/api/notifications/${notificationId}/read`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setNotifications((prev) =>
                prev.map((notif) => (notif.id === notificationId ? { ...notif, is_read: true } : notif))
            );
        } catch (err) {
            console.error('Mark as read failed:', err);
        }
    };

    const clearNotification = async (notificationId, event) => {
        event.stopPropagation();
        try {
            await axios.delete(`${API_BASE}/api/notifications/${notificationId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        } catch (err) {
            console.error('Delete notification failed:', err);
        }
    };

    const markAllRead = async () => {
        try {
            await axios.put(
                `${API_BASE}/api/notifications/read-all`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        } catch (err) {
            console.error('Mark all as read failed:', err);
        }
    };

    const unreadCount = notifications.filter((n) => !n.is_read).length;

    const formatTimestamp = (ts) => {
        if (!ts) return '';
        return new Date(ts).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const handleMouseEnter = () => {
        if (hideTimeoutId) clearTimeout(hideTimeoutId);
        setNotificationVisible(true);
    };

    const handleMouseLeave = () => {
        const timeoutId = setTimeout(() => {
            setNotificationVisible(false);
        }, 300);
        setHideTimeoutId(timeoutId);
    };

    return (
        <>
            <ToastContainer />
            <GlobalSearchModal
                open={searchOpen}
                onClose={() => setSearchOpen(false)}
                apiBase={API_BASE}
                getToken={getToken}
                searchPath="/api/hr/search"
                placeholder="Search employees by name, email, or emp code…"
            />
            <nav className="navbar-container">
                <div className="navbar-left" style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                    <AppLauncher currentApp="ZHR" ztoolUrl={ZTOOL_URL} />
                    {ZTOOL_URL && (
                        <a
                            href={`${String(ZTOOL_URL).replace(/\/$/, '')}/dashboard`}
                            className="crane-ztools-back"
                            title="Return to CraneSoftwares launcher"
                        >
                            ← ZTools
                        </a>
                    )}
                    <Link to="/" className="navbar-logo-link" aria-label="CraneSoftwares home">
                        <BrandLogo src={platformLogo} size="sm" alt="CraneSoftwares" />
                    </Link>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#006194', flexShrink: 0 }}>ZHR</span>
                    {tenantName ? (
                        <div className="crane-tenant-chip" title="Registered company / tenant">
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {tenantName}
                            </span>
                        </div>
                    ) : null}
                    <button
                        type="button"
                        className="crane-search-trigger"
                        style={{ flex: 1, minWidth: 160, maxWidth: 480 }}
                        onClick={() => setSearchOpen(true)}
                        aria-label="Open global search"
                    >
                        <span>Search employees…</span>
                        <span className="crane-search-trigger__kbd">Ctrl+K</span>
                    </button>
                </div>
                {userId && (
                    <div className="navbar-right">
                        <div
                            className="notification-wrapper"
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <button className="navbar-icon-button" type="button" aria-label="Notifications">
                                <FontAwesomeIcon icon={faBell} />
                                {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
                            </button>
                            {isNotificationVisible && (
                                <div className="notification-panel">
                                    <div className="notification-header">
                                        <h3>Notifications</h3>
                                        {unreadCount > 0 && (
                                            <button onClick={markAllRead} className="clear-all-btn" type="button">
                                                <FontAwesomeIcon icon={faCheckDouble} style={{ marginRight: '4px' }} />{' '}
                                                Mark all read
                                            </button>
                                        )}
                                    </div>
                                    <div className="notification-list">
                                        {notifications.length > 0 ? (
                                            notifications.map((notif) => (
                                                <div
                                                    key={notif.id}
                                                    className={`notification-item ${
                                                        notif.is_read ? 'notification-item--seen' : ''
                                                    } notification-item--clickable`}
                                                    onClick={() => handleNotificationClick()}
                                                >
                                                    <div className="notification-content">
                                                        <p>{notif.message}</p>
                                                        <span className="notification-timestamp">
                                                            {formatTimestamp(notif.created_at || notif.created_date)}
                                                        </span>
                                                    </div>
                                                    <div className="notification-actions">
                                                        {!notif.is_read && (
                                                            <button
                                                                onClick={(e) => markAsSeen(notif.id, e)}
                                                                className="mark-as-read-btn"
                                                                title="Mark as read"
                                                                type="button"
                                                            >
                                                                Read
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={(e) => clearNotification(notif.id, e)}
                                                            className="delete-notification-btn"
                                                            title="Delete"
                                                            type="button"
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="notification-item-empty">
                                                <p>No new notifications</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </nav>
        </>
    );
};

export default Navbar;
